import { useState } from "react";

interface ReportCard {
  id: string;
  title: string;
  icon: string;
  color: string;
  hoverColor: string;
  textColor: string;
}

const cards: ReportCard[] = [
  {
    id: "products",
    title: "ÜRÜNLER",
    icon: "ri-price-tag-3-line",
    color: "bg-accent-600",
    hoverColor: "hover:bg-accent-700",
    textColor: "text-background-50",
  },
  {
    id: "movements",
    title: "STOK HAREKETLERİ",
    icon: "ri-truck-line",
    color: "bg-primary-600",
    hoverColor: "hover:bg-primary-700",
    textColor: "text-background-50",
  },
  {
    id: "warehouse",
    title: "DEPO DURUMU",
    icon: "ri-bar-chart-box-line",
    color: "bg-secondary-700",
    hoverColor: "hover:bg-secondary-800",
    textColor: "text-background-50",
  },
  {
    id: "stock-sales",
    title: "STOK-SATIŞ KARŞILAMA",
    icon: "ri-arrow-left-right-line",
    color: "bg-accent-400",
    hoverColor: "hover:bg-accent-500",
    textColor: "text-background-50",
  },
  {
    id: "inactive",
    title: "HAREKET GÖRMEYEN ÜRÜNLER",
    icon: "ri-emotion-unhappy-line",
    color: "bg-primary-500",
    hoverColor: "hover:bg-primary-600",
    textColor: "text-background-50",
  },
];

export default function StockReportsPage() {
  const [activeCard, setActiveCard] = useState<string | null>(null);

  const handleCardClick = (id: string) => {
    setActiveCard(id === activeCard ? null : id);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-foreground-950">Stok Raporları</h1>
        <p className="text-sm text-foreground-500 mt-0.5">
          Stok raporları menüsünden görüntülemek istediğiniz raporu seçin
        </p>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Row 1: Ürünler (col-span-2) + Stok Hareketleri (col-span-2) */}
        {cards.slice(0, 2).map((card) => (
          <button
            key={card.id}
            onClick={() => handleCardClick(card.id)}
            className={`${card.color} ${card.hoverColor} ${card.textColor} col-span-1 sm:col-span-1 lg:col-span-2 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center gap-3 sm:gap-4 transition-all duration-200 cursor-pointer active:scale-[0.98] min-h-[140px] sm:min-h-[160px]`}
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">
              <i className={`${card.icon} text-3xl sm:text-4xl`}></i>
            </div>
            <span className="text-sm sm:text-base font-bold tracking-wide text-center">
              {card.title}
            </span>
          </button>
        ))}

        {/* Row 2: Depo Durumu (col-span-2) + Stok-Satış (col-span-1) + Hareket Görmeyen (col-span-1) */}
        <button
          onClick={() => handleCardClick(cards[2].id)}
          className={`${cards[2].color} ${cards[2].hoverColor} ${cards[2].textColor} col-span-1 sm:col-span-1 lg:col-span-2 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center gap-3 sm:gap-4 transition-all duration-200 cursor-pointer active:scale-[0.98] min-h-[140px] sm:min-h-[160px]`}
        >
          <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">
            <i className={`${cards[2].icon} text-3xl sm:text-4xl`}></i>
          </div>
          <span className="text-sm sm:text-base font-bold tracking-wide text-center">
            {cards[2].title}
          </span>
        </button>

        {cards.slice(3).map((card) => (
          <button
            key={card.id}
            onClick={() => handleCardClick(card.id)}
            className={`${card.color} ${card.hoverColor} ${card.textColor} col-span-1 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center gap-3 sm:gap-4 transition-all duration-200 cursor-pointer active:scale-[0.98] min-h-[140px] sm:min-h-[160px]`}
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">
              <i className={`${card.icon} text-3xl sm:text-4xl`}></i>
            </div>
            <span className="text-sm sm:text-base font-bold tracking-wide text-center">
              {card.title}
            </span>
          </button>
        ))}
      </div>

      {/* Active Report Content */}
      {activeCard === "products" && <ProductsReport />}
      {activeCard === "movements" && <MovementsReport />}
      {activeCard === "warehouse" && <WarehouseReport />}
      {activeCard === "stock-sales" && <StockSalesReport />}
      {activeCard === "inactive" && <InactiveProductsReport />}
    </div>
  );
}

/* Ürünler Report */
function ProductsReport() {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [productStatus, setProductStatus] = useState("all");
  const [shelfLocation, setShelfLocation] = useState("");

  const categories = ["Elektronik", "Giyim", "Gıda", "Aksesuar", "Ofis"];
  const brands = ["Logitech", "Samsung", "Apple", "Dell", "HP", "Corsair"];
  const shelves = ["A1", "A2", "B1", "B2", "C1", "Depo"];

  const data = [
    { name: "Kablosuz Mouse", code: "PRD-001", category: "Elektronik", brand: "Logitech", stock: 45, minStock: 10, shelf: "A1", status: "active" },
    { name: "Mechanik Klavye", code: "PRD-002", category: "Elektronik", brand: "Corsair", stock: 32, minStock: 5, shelf: "A2", status: "active" },
    { name: "27\" Monitör", code: "PRD-003", category: "Elektronik", brand: "Samsung", stock: 12, minStock: 3, shelf: "B1", status: "active" },
    { name: "USB-C Hub", code: "PRD-004", category: "Aksesuar", brand: "Dell", stock: 88, minStock: 15, shelf: "A1", status: "active" },
    { name: "Webcam 1080p", code: "PRD-005", category: "Elektronik", brand: "Logitech", stock: 24, minStock: 8, shelf: "B2", status: "active" },
    { name: "Laptop Standı", code: "PRD-006", category: "Aksesuar", brand: "HP", stock: 56, minStock: 10, shelf: "C1", status: "passive" },
  ].filter((i) => {
    if (search && !i.name.toLowerCase().includes(search.toLowerCase()) && !i.code.toLowerCase().includes(search.toLowerCase())) return false;
    if (selectedCategory && i.category !== selectedCategory) return false;
    if (selectedBrand && i.brand !== selectedBrand) return false;
    if (productStatus !== "all" && i.status !== productStatus) return false;
    if (shelfLocation && i.shelf !== shelfLocation) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Filter Panel */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        <div className="bg-primary-600 px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-semibold text-background-50">Ürün Listesi Rapor Kriterleri</span>
          <button
            onClick={() => {
              setSearch("");
              setSelectedCategory("");
              setSelectedBrand("");
              setProductStatus("all");
              setShelfLocation("");
            }}
            className="text-xs text-background-50/80 hover:text-background-50 transition-colors cursor-pointer"
          >
            Temizle
          </button>
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Ürün</label>
            <div className="relative">
              <i className="ri-barcode-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="text"
                placeholder="Ürün isminden arayın veya barkod okutun"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Ürün Durumu</label>
            <select
              value={productStatus}
              onChange={(e) => setProductStatus(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="all">Tüm ürünler</option>
              <option value="active">Aktif ürünler</option>
              <option value="passive">Pasif ürünler</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Kategori</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm kategoriler</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Marka</label>
            <select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm markalar</option>
              {brands.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Raf Yeri</label>
            <select
              value={shelfLocation}
              onChange={(e) => setShelfLocation(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm raflar</option>
              {shelves.map((s) => (
                <option key={s} value={s}>Raf {s}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground-950">Ürün Listesi</h2>
        <span className="text-sm text-foreground-500">{data.length} ürün bulundu</span>
      </div>
      <div className="hidden lg:block bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-background-100 border-b border-background-200/70">
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Ürün</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Kod</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Kategori</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Marka</th>
              <th className="px-4 py-3 text-center font-medium text-foreground-600">Stok</th>
              <th className="px-4 py-3 text-center font-medium text-foreground-600">Raf</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.code} className="border-b border-background-200/50 hover:bg-background-100/50 transition-colors">
                <td className="px-4 py-3 font-medium text-foreground-900">{item.name}</td>
                <td className="px-4 py-3 text-foreground-500">{item.code}</td>
                <td className="px-4 py-3 text-foreground-500">{item.category}</td>
                <td className="px-4 py-3 text-foreground-500">{item.brand}</td>
                <td className="px-4 py-3 text-center font-semibold text-foreground-900">{item.stock}</td>
                <td className="px-4 py-3 text-center text-foreground-500">{item.shelf}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden space-y-3">
        {data.map((item) => (
          <div key={item.code} className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                <p className="text-xs text-foreground-400 mt-0.5">{item.code} · {item.category} · {item.brand}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-foreground-900">{item.stock}</p>
                <p className="text-xs text-foreground-400">Adet</p>
              </div>
            </div>
            <p className="text-xs text-foreground-500 mt-2">Raf: {item.shelf}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Stok Hareketleri Report */
function MovementsReport() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [warehouse, setWarehouse] = useState("");
  const [transactionType, setTransactionType] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [shelf, setShelf] = useState("");

  const warehouses = ["Ana Depo", "İstanbul Şube", "İzmir Şube", "Ankara Şube"];
  const types = ["Giriş", "Çıkış", "Transfer", "Sayım Farkı"];
  const categories = ["Elektronik", "Giyim", "Gıda", "Aksesuar", "Ofis"];
  const brands = ["Logitech", "Samsung", "Apple", "Dell", "HP", "Corsair"];
  const shelves = ["A1", "A2", "B1", "B2", "C1", "Depo"];

  const allData = [
    { date: "2026-07-17", product: "Kablosuz Mouse", category: "Elektronik", brand: "Logitech", type: "Giriş", quantity: 50, dealer: "Ana Bayi", warehouse: "Ana Depo", shelf: "A1" },
    { date: "2026-07-16", product: "Mechanik Klavye", category: "Elektronik", brand: "Corsair", type: "Çıkış", quantity: 12, dealer: "Teknoloji Dünyası", warehouse: "İstanbul Şube", shelf: "A2" },
    { date: "2026-07-16", product: "27\" Monitör", category: "Elektronik", brand: "Samsung", type: "Giriş", quantity: 20, dealer: "Ana Bayi", warehouse: "Ana Depo", shelf: "B1" },
    { date: "2026-07-15", product: "USB-C Hub", category: "Aksesuar", brand: "Dell", type: "Çıkış", quantity: 8, dealer: "Bilgisayar Plaza", warehouse: "İzmir Şube", shelf: "A1" },
    { date: "2026-07-15", product: "Webcam 1080p", category: "Elektronik", brand: "Logitech", type: "Giriş", quantity: 30, dealer: "Ana Bayi", warehouse: "Ana Depo", shelf: "B2" },
    { date: "2026-07-14", product: "Laptop Standı", category: "Aksesuar", brand: "HP", type: "Çıkış", quantity: 15, dealer: "Ofis Market", warehouse: "Ankara Şube", shelf: "C1" },
    { date: "2026-07-14", product: "VGA Kablo 5m", category: "Aksesuar", brand: "Dell", type: "Transfer", quantity: 10, dealer: "Ana Bayi", warehouse: "İstanbul Şube", shelf: "Depo" },
    { date: "2026-07-13", product: "Masa Lambası LED", category: "Ofis", brand: "Samsung", type: "Sayım Farkı", quantity: -2, dealer: "Ana Bayi", warehouse: "Ana Depo", shelf: "C1" },
  ];

  const data = allData.filter((i) => {
    if (startDate && i.date < startDate) return false;
    if (endDate && i.date > endDate) return false;
    if (warehouse && i.warehouse !== warehouse) return false;
    if (transactionType && i.type !== transactionType) return false;
    if (search && !i.product.toLowerCase().includes(search.toLowerCase())) return false;
    if (category && i.category !== category) return false;
    if (brand && i.brand !== brand) return false;
    if (shelf && i.shelf !== shelf) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Filter Panel */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        <div className="bg-primary-600 px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-semibold text-background-50">Stok Hareketleri Rapor Kriterleri</span>
          <button
            onClick={() => {
              setStartDate("");
              setEndDate("");
              setWarehouse("");
              setTransactionType("");
              setSearch("");
              setCategory("");
              setBrand("");
              setShelf("");
            }}
            className="text-xs text-background-50/80 hover:text-background-50 transition-colors cursor-pointer"
          >
            Temizle
          </button>
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="sm:col-span-2 lg:col-span-2">
            <label className="block text-xs font-medium text-foreground-600 mb-1">Tarih Aralığı</label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex-1 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <span className="text-sm text-foreground-400">-</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex-1 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Depo</label>
            <select
              value={warehouse}
              onChange={(e) => setWarehouse(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm depolar</option>
              {warehouses.map((w) => (
                <option key={w} value={w}>{w}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">İşlem Tipi</label>
            <select
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm işlem tipleri</option>
              {types.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Ürün</label>
            <div className="relative">
              <i className="ri-barcode-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="text"
                placeholder="Ürün isminden arayın veya barkod okutun"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Kategori</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm kategoriler</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Marka</label>
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm markalar</option>
              {brands.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Raf Yeri</label>
            <select
              value={shelf}
              onChange={(e) => setShelf(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm raflar</option>
              {shelves.map((s) => (
                <option key={s} value={s}>Raf {s}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground-950">Stok Hareketleri</h2>
        <span className="text-sm text-foreground-500">{data.length} kayıt bulundu</span>
      </div>
      <div className="hidden lg:block bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-background-100 border-b border-background-200/70">
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Tarih</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Ürün</th>
              <th className="px-4 py-3 text-center font-medium text-foreground-600">Tür</th>
              <th className="px-4 py-3 text-center font-medium text-foreground-600">Miktar</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Depo</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Bayi</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, idx) => (
              <tr key={idx} className="border-b border-background-200/50 hover:bg-background-100/50 transition-colors">
                <td className="px-4 py-3 text-foreground-500">{item.date}</td>
                <td className="px-4 py-3 font-medium text-foreground-900">{item.product}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${item.type === "Giriş" ? "bg-primary-100 text-primary-700" : item.type === "Çıkış" ? "bg-accent-100 text-accent-700" : item.type === "Transfer" ? "bg-secondary-100 text-secondary-700" : "bg-background-100 text-foreground-500"}`}>
                    <i className={item.type === "Giriş" ? "ri-arrow-down-line" : item.type === "Çıkış" ? "ri-arrow-up-line" : item.type === "Transfer" ? "ri-arrow-left-right-line" : "ri-file-list-line"}></i>
                    {item.type}
                  </span>
                </td>
                <td className="px-4 py-3 text-center font-semibold text-foreground-900">{item.quantity}</td>
                <td className="px-4 py-3 text-foreground-500">{item.warehouse}</td>
                <td className="px-4 py-3 text-foreground-500">{item.dealer}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden space-y-3">
        {data.map((item, idx) => (
          <div key={idx} className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-foreground-900">{item.product}</p>
                <p className="text-xs text-foreground-400 mt-0.5">{item.date} · {item.warehouse}</p>
              </div>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${item.type === "Giriş" ? "bg-primary-100 text-primary-700" : item.type === "Çıkış" ? "bg-accent-100 text-accent-700" : item.type === "Transfer" ? "bg-secondary-100 text-secondary-700" : "bg-background-100 text-foreground-500"}`}>
                {item.type}
              </span>
            </div>
            <p className="text-sm font-semibold text-foreground-900 mt-2">{item.quantity} adet · {item.dealer}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Depo Durumu Report */
function WarehouseReport() {
  const [search, setSearch] = useState("");
  const [warehouse, setWarehouse] = useState("");
  const [showImages, setShowImages] = useState("hide");
  const [stockDate, setStockDate] = useState("");
  const [variant, setVariant] = useState("");
  const [productStatus, setProductStatus] = useState("all");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [shelf, setShelf] = useState("");
  const [stockStatus, setStockStatus] = useState("in_stock");

  const warehouses = ["Ana Depo", "İstanbul Şube", "İzmir Şube", "Ankara Şube"];
  const categories = ["Elektronik", "Giyim", "Gıda", "Aksesuar", "Ofis"];
  const brands = ["Logitech", "Samsung", "Apple", "Dell", "HP", "Corsair"];
  const shelves = ["A1", "A2", "B1", "B2", "C1", "Depo"];

  const allData = [
    { warehouse: "Ana Depo", name: "Kablosuz Mouse", code: "PRD-001", category: "Elektronik", brand: "Logitech", stock: 45, minStock: 10, shelf: "A1", status: "active", variant: "Siyah", image: "https://readdy.ai/api/search-image?query=Black%20wireless%20computer%20mouse%20product%20photo%20on%20white%20background&width=100&height=100&seq=prod-01&orientation=squarish" },
    { warehouse: "Ana Depo", name: "Mechanik Klavye", code: "PRD-002", category: "Elektronik", brand: "Corsair", stock: 32, minStock: 5, shelf: "A2", status: "active", variant: "RGB", image: "https://readdy.ai/api/search-image?query=Mechanical%20gaming%20keyboard%20RGB%20product%20photo%20on%20white%20background&width=100&height=100&seq=prod-02&orientation=squarish" },
    { warehouse: "Ana Depo", name: "27\" Monitör", code: "PRD-003", category: "Elektronik", brand: "Samsung", stock: 12, minStock: 3, shelf: "B1", status: "active", variant: "4K", image: "https://readdy.ai/api/search-image?query=27%20inch%20computer%20monitor%20product%20photo%20on%20white%20background&width=100&height=100&seq=prod-03&orientation=squarish" },
    { warehouse: "İstanbul Şube", name: "USB-C Hub", code: "PRD-004", category: "Aksesuar", brand: "Dell", stock: 88, minStock: 15, shelf: "A1", status: "active", variant: "7in1", image: "https://readdy.ai/api/search-image?query=USB%20C%20hub%20adapter%20product%20photo%20on%20white%20background&width=100&height=100&seq=prod-04&orientation=squarish" },
    { warehouse: "İstanbul Şube", name: "Webcam 1080p", code: "PRD-005", category: "Elektronik", brand: "Logitech", stock: 24, minStock: 8, shelf: "B2", status: "active", variant: "HD", image: "https://readdy.ai/api/search-image?query=Webcam%20camera%20product%20photo%20on%20white%20background&width=100&height=100&seq=prod-05&orientation=squarish" },
    { warehouse: "Ankara Şube", name: "Laptop Standı", code: "PRD-006", category: "Aksesuar", brand: "HP", stock: 56, minStock: 10, shelf: "C1", status: "passive", variant: "Alüminyum", image: "https://readdy.ai/api/search-image?query=Aluminum%20laptop%20stand%20product%20photo%20on%20white%20background&width=100&height=100&seq=prod-06&orientation=squarish" },
  ];

  const data = allData.filter((i) => {
    if (search && !i.name.toLowerCase().includes(search.toLowerCase()) && !i.code.toLowerCase().includes(search.toLowerCase())) return false;
    if (warehouse && i.warehouse !== warehouse) return false;
    if (productStatus !== "all" && i.status !== productStatus) return false;
    if (category && i.category !== category) return false;
    if (brand && i.brand !== brand) return false;
    if (shelf && i.shelf !== shelf) return false;
    if (stockStatus === "in_stock" && i.stock === 0) return false;
    if (stockStatus === "out_of_stock" && i.stock > 0) return false;
    if (variant && !i.variant.toLowerCase().includes(variant.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Filter Panel */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        <div className="bg-primary-600 px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-semibold text-background-50">Depo Durumu Rapor Kriterleri</span>
          <button
            onClick={() => {
              setSearch("");
              setWarehouse("");
              setShowImages("hide");
              setStockDate("");
              setVariant("");
              setProductStatus("all");
              setCategory("");
              setBrand("");
              setShelf("");
              setStockStatus("in_stock");
            }}
            className="text-xs text-background-50/80 hover:text-background-50 transition-colors cursor-pointer"
          >
            Temizle
          </button>
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Ürün</label>
            <div className="relative">
              <i className="ri-barcode-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="text"
                placeholder="Ürün isminden arayın veya barkod okutun"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Ürün Durumu</label>
            <select
              value={productStatus}
              onChange={(e) => setProductStatus(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="all">Tüm ürünler</option>
              <option value="active">Aktif ürünler</option>
              <option value="passive">Pasif ürünler</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Depo</label>
            <select
              value={warehouse}
              onChange={(e) => setWarehouse(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm depolar</option>
              {warehouses.map((w) => (
                <option key={w} value={w}>{w}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Kategori</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm kategoriler</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Ürün Resimleri</label>
            <select
              value={showImages}
              onChange={(e) => setShowImages(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="hide">Ürün resimlerini gösterme</option>
              <option value="show">Ürün resimlerini göster</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Marka</label>
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm markalar</option>
              {brands.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Stok Tarihi</label>
            <input
              type="date"
              value={stockDate}
              onChange={(e) => setStockDate(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
            <p className="text-xs text-foreground-400 mt-1">Seçtiğiniz tarih itibariyle olan stokları gösterir.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Raf Yeri</label>
            <select
              value={shelf}
              onChange={(e) => setShelf(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm raflar</option>
              {shelves.map((s) => (
                <option key={s} value={s}>Raf {s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Varyant</label>
            <select
              value={variant}
              onChange={(e) => setVariant(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm varyantlar</option>
              <option value="Siyah">Siyah</option>
              <option value="Beyaz">Beyaz</option>
              <option value="RGB">RGB</option>
              <option value="4K">4K</option>
              <option value="HD">HD</option>
              <option value="Alüminyum">Alüminyum</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Stok Durumu</label>
            <select
              value={stockStatus}
              onChange={(e) => setStockStatus(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="all">Tüm ürünleri göster</option>
              <option value="in_stock">Stokta olanları göster</option>
              <option value="out_of_stock">Stokta olmayanları göster</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground-950">Depo Durumu</h2>
        <span className="text-sm text-foreground-500">{data.length} ürün bulundu</span>
      </div>

      <div className="hidden lg:block bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-background-100 border-b border-background-200/70">
              {showImages === "show" && <th className="px-4 py-3 text-center font-medium text-foreground-600 w-16">Resim</th>}
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Depo</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Ürün</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Kod</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Kategori</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Marka</th>
              <th className="px-4 py-3 text-center font-medium text-foreground-600">Stok</th>
              <th className="px-4 py-3 text-center font-medium text-foreground-600">Raf</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.code} className="border-b border-background-200/50 hover:bg-background-100/50 transition-colors">
                {showImages === "show" && (
                  <td className="px-4 py-3 text-center">
                    <img src={item.image} alt={item.name} className="w-10 h-10 rounded-lg object-cover" />
                  </td>
                )}
                <td className="px-4 py-3 text-foreground-500">{item.warehouse}</td>
                <td className="px-4 py-3 font-medium text-foreground-900">{item.name}</td>
                <td className="px-4 py-3 text-foreground-500">{item.code}</td>
                <td className="px-4 py-3 text-foreground-500">{item.category}</td>
                <td className="px-4 py-3 text-foreground-500">{item.brand}</td>
                <td className="px-4 py-3 text-center font-semibold text-foreground-900">{item.stock}</td>
                <td className="px-4 py-3 text-center text-foreground-500">{item.shelf}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="lg:hidden space-y-3">
        {data.map((item) => (
          <div key={item.code} className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <div className="flex items-start gap-3">
              {showImages === "show" && (
                <img src={item.image} alt={item.name} className="w-12 h-12 rounded-lg object-cover shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                <p className="text-xs text-foreground-400 mt-0.5">{item.code} · {item.category} · {item.brand}</p>
                <p className="text-xs text-foreground-400">{item.warehouse} · Raf {item.shelf}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-semibold text-foreground-900">{item.stock}</p>
                <p className="text-xs text-foreground-400">Adet</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Stok-Satış Karşılama Report */
function StockSalesReport() {
  const [searchType, setSearchType] = useState("doc_date");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [docStatus, setDocStatus] = useState("invoiced");
  const [stockStatus, setStockStatus] = useState("all");
  const [searchProduct, setSearchProduct] = useState("");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [warehouse, setWarehouse] = useState("");

  const categories = ["Elektronik", "Giyim", "Gıda", "Aksesuar", "Ofis"];
  const brands = ["Logitech", "Samsung", "Apple", "Dell", "HP", "Corsair"];
  const warehouses = ["Ana Depo", "İstanbul Şube", "İzmir Şube", "Ankara Şube"];

  const allData = [
    { product: "Kablosuz Mouse", category: "Elektronik", brand: "Logitech", stock: 45, sold: 38, match: 84, warehouse: "Ana Depo", docStatus: "invoiced" },
    { product: "Mechanik Klavye", category: "Elektronik", brand: "Corsair", stock: 32, sold: 28, match: 88, warehouse: "Ana Depo", docStatus: "invoiced" },
    { product: "27\" Monitör", category: "Elektronik", brand: "Samsung", stock: 12, sold: 15, match: 125, warehouse: "Ana Depo", docStatus: "pending" },
    { product: "USB-C Hub", category: "Aksesuar", brand: "Dell", stock: 88, sold: 42, match: 48, warehouse: "İstanbul Şube", docStatus: "invoiced" },
    { product: "Webcam 1080p", category: "Elektronik", brand: "Logitech", stock: 24, sold: 22, match: 92, warehouse: "Ana Depo", docStatus: "invoiced" },
    { product: "Laptop Standı", category: "Aksesuar", brand: "HP", stock: 56, sold: 31, match: 55, warehouse: "Ankara Şube", docStatus: "draft" },
    { product: "VGA Kablo 5m", category: "Aksesuar", brand: "Dell", stock: 0, sold: 5, match: 0, warehouse: "İzmir Şube", docStatus: "invoiced" },
    { product: "Masa Lambası LED", category: "Ofis", brand: "Samsung", stock: 8, sold: 12, match: 150, warehouse: "Ana Depo", docStatus: "pending" },
  ];

  const data = allData.filter((i) => {
    if (searchProduct && !i.product.toLowerCase().includes(searchProduct.toLowerCase())) return false;
    if (category && i.category !== category) return false;
    if (brand && i.brand !== brand) return false;
    if (warehouse && i.warehouse !== warehouse) return false;
    if (docStatus !== "all" && i.docStatus !== docStatus) return false;
    if (stockStatus === "in_stock" && i.stock === 0) return false;
    if (stockStatus === "out_of_stock" && i.stock > 0) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Filter Panel */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        <div className="bg-primary-600 px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-semibold text-background-50">Stok Karşılama Rapor Kriterleri</span>
          <button
            onClick={() => {
              setSearchType("doc_date");
              setStartDate("");
              setEndDate("");
              setDocStatus("invoiced");
              setStockStatus("all");
              setSearchProduct("");
              setCategory("");
              setBrand("");
              setWarehouse("");
            }}
            className="text-xs text-background-50/80 hover:text-background-50 transition-colors cursor-pointer"
          >
            Temizle
          </button>
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Arama</label>
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="doc_date">Belge tarihine göre ara</option>
              <option value="product_name">Ürün adına göre ara</option>
              <option value="doc_no">Belge numarasına göre ara</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Ürün / Hizmet</label>
            <div className="relative">
              <i className="ri-barcode-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="text"
                placeholder="Ürün isminden arayın veya barkod okutun"
                value={searchProduct}
                onChange={(e) => setSearchProduct(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-foreground-600 mb-1">Tarih Aralığı</label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex-1 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <span className="text-sm text-foreground-400">-</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex-1 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Kategori</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm kategoriler</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Belge Durumu</label>
            <select
              value={docStatus}
              onChange={(e) => setDocStatus(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="all">Tüm belgeler</option>
              <option value="invoiced">Faturalanmış</option>
              <option value="pending">Bekliyor</option>
              <option value="draft">Taslak</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Marka</label>
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm markalar</option>
              {brands.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Stok Durumu</label>
            <select
              value={stockStatus}
              onChange={(e) => setStockStatus(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="all">Hepsini göster</option>
              <option value="in_stock">Stokta olanları göster</option>
              <option value="out_of_stock">Stokta olmayanları göster</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Depo</label>
            <select
              value={warehouse}
              onChange={(e) => setWarehouse(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm depolar</option>
              {warehouses.map((w) => (
                <option key={w} value={w}>{w}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground-950">Stok-Satış Karşılama</h2>
        <span className="text-sm text-foreground-500">{data.length} ürün bulundu</span>
      </div>
      <div className="hidden lg:block bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-background-100 border-b border-background-200/70">
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Ürün</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Kategori</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Marka</th>
              <th className="px-4 py-3 text-center font-medium text-foreground-600">Mevcut Stok</th>
              <th className="px-4 py-3 text-center font-medium text-foreground-600">Satış (30 gün)</th>
              <th className="px-4 py-3 text-center font-medium text-foreground-600">Karşılama Oranı</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Depo</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.product} className="border-b border-background-200/50 hover:bg-background-100/50 transition-colors">
                <td className="px-4 py-3 font-medium text-foreground-900">{item.product}</td>
                <td className="px-4 py-3 text-foreground-500">{item.category}</td>
                <td className="px-4 py-3 text-foreground-500">{item.brand}</td>
                <td className="px-4 py-3 text-center font-semibold text-foreground-900">{item.stock}</td>
                <td className="px-4 py-3 text-center text-foreground-500">{item.sold}</td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-20 bg-background-200/50 rounded-full h-2 overflow-hidden">
                      <div className={`h-full rounded-full ${item.match >= 100 ? "bg-accent-500" : item.match >= 70 ? "bg-primary-500" : "bg-secondary-500"}`} style={{ width: `${Math.min(item.match, 100)}%` }}></div>
                    </div>
                    <span className={`text-xs font-medium ${item.match >= 100 ? "text-accent-600" : item.match >= 70 ? "text-primary-600" : "text-secondary-600"}`}>
                      {item.match}%
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-foreground-500">{item.warehouse}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden space-y-3">
        {data.map((item) => (
          <div key={item.product} className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <p className="text-sm font-medium text-foreground-900">{item.product}</p>
            <p className="text-xs text-foreground-400">{item.category} · {item.brand} · {item.warehouse}</p>
            <div className="flex items-center justify-between mt-2">
              <div className="flex gap-4">
                <div>
                  <p className="text-xs text-foreground-400">Stok</p>
                  <p className="text-sm font-semibold text-foreground-900">{item.stock}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">Satış</p>
                  <p className="text-sm font-semibold text-foreground-900">{item.sold}</p>
                </div>
              </div>
              <span className={`text-sm font-bold ${item.match >= 100 ? "text-accent-600" : item.match >= 70 ? "text-primary-600" : "text-secondary-600"}`}>
                {item.match}%
              </span>
            </div>
            <div className="w-full bg-background-200/50 rounded-full h-2 overflow-hidden mt-2">
              <div className={`h-full rounded-full ${item.match >= 100 ? "bg-accent-500" : item.match >= 70 ? "bg-primary-500" : "bg-secondary-500"}`} style={{ width: `${Math.min(item.match, 100)}%` }}></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Hareket Görmeyen Ürünler Report */
function InactiveProductsReport() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [warehouse, setWarehouse] = useState("");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [productStatus, setProductStatus] = useState("all");
  const [minDays, setMinDays] = useState(30);
  const [search, setSearch] = useState("");

  const warehouses = ["Ana Depo", "İstanbul Şube", "İzmir Şube", "Ankara Şube"];
  const categories = ["Elektronik", "Giyim", "Gıda", "Aksesuar", "Ofis"];
  const brands = ["Logitech", "Samsung", "Apple", "Dell", "HP", "Corsair"];

  const allData = [
    { name: "VGA Kablo 5m", code: "PRD-089", category: "Aksesuar", brand: "Dell", stock: 10, warehouse: "Ana Depo", lastMovement: "2026-04-12", daysAgo: 96, status: "active" },
    { name: "A4 Kağıt Paketi", code: "PRD-112", category: "Ofis", brand: "HP", stock: 200, warehouse: "İstanbul Şube", lastMovement: "2026-05-20", daysAgo: 58, status: "active" },
    { name: "Masa Lambası LED", code: "PRD-145", category: "Ofis", brand: "Samsung", stock: 8, warehouse: "Ana Depo", lastMovement: "2026-05-28", daysAgo: 50, status: "passive" },
    { name: "Kablosuz Şarj Standı", code: "PRD-167", category: "Elektronik", brand: "Apple", stock: 15, warehouse: "İzmir Şube", lastMovement: "2026-06-05", daysAgo: 42, status: "active" },
    { name: "Mouse Pad XL", code: "PRD-201", category: "Aksesuar", brand: "Logitech", stock: 34, warehouse: "Ankara Şube", lastMovement: "2026-06-10", daysAgo: 37, status: "active" },
    { name: "Hoparlör 2.1", code: "PRD-234", category: "Elektronik", brand: "Samsung", stock: 0, warehouse: "Ana Depo", lastMovement: "2026-03-15", daysAgo: 124, status: "passive" },
    { name: "Ethernet Kablosu 10m", code: "PRD-256", category: "Aksesuar", brand: "Dell", stock: 45, warehouse: "İstanbul Şube", lastMovement: "2026-06-01", daysAgo: 46, status: "active" },
    { name: "Beyaz Tahta Kalemi", code: "PRD-278", category: "Ofis", brand: "HP", stock: 120, warehouse: "Ankara Şube", lastMovement: "2026-04-25", daysAgo: 83, status: "active" },
    { name: "HDMI Switch 3 Port", code: "PRD-301", category: "Elektronik", brand: "Logitech", stock: 7, warehouse: "İzmir Şube", lastMovement: "2026-05-10", daysAgo: 68, status: "passive" },
    { name: "Notebook Çantası", code: "PRD-333", category: "Giyim", brand: "Corsair", stock: 22, warehouse: "Ana Depo", lastMovement: "2026-06-08", daysAgo: 39, status: "active" },
  ];

  const data = allData.filter((i) => {
    if (search && !i.name.toLowerCase().includes(search.toLowerCase()) && !i.code.toLowerCase().includes(search.toLowerCase())) return false;
    if (startDate && i.lastMovement > startDate) return false;
    if (endDate && i.lastMovement < endDate) return false;
    if (warehouse && i.warehouse !== warehouse) return false;
    if (category && i.category !== category) return false;
    if (brand && i.brand !== brand) return false;
    if (productStatus !== "all" && i.status !== productStatus) return false;
    if (i.daysAgo < minDays) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Filter Panel */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        <div className="bg-primary-600 px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-semibold text-background-50">Hareket Görmeyen Ürünler Rapor Kriterleri</span>
          <button
            onClick={() => {
              setStartDate("");
              setEndDate("");
              setWarehouse("");
              setCategory("");
              setBrand("");
              setProductStatus("all");
              setMinDays(30);
              setSearch("");
            }}
            className="text-xs text-background-50/80 hover:text-background-50 transition-colors cursor-pointer"
          >
            Temizle
          </button>
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-foreground-600 mb-1">Tarih Aralığı</label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex-1 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <span className="text-sm text-foreground-400">-</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex-1 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Depo</label>
            <select
              value={warehouse}
              onChange={(e) => setWarehouse(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm depolar</option>
              {warehouses.map((w) => (
                <option key={w} value={w}>{w}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Kategori</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm kategoriler</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Marka</label>
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Tüm markalar</option>
              {brands.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Ürün Durumu</label>
            <select
              value={productStatus}
              onChange={(e) => setProductStatus(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="all">Tüm ürünler</option>
              <option value="active">Aktif ürünler</option>
              <option value="passive">Pasif ürünler</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-foreground-600 mb-1">Minimum Hareketsizlik Süresi</label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={7}
                max={180}
                step={1}
                value={minDays}
                onChange={(e) => setMinDays(Number(e.target.value))}
                className="flex-1 accent-primary-500"
              />
              <span className="text-sm font-semibold text-foreground-900 w-16 text-right whitespace-nowrap">{minDays} gün</span>
            </div>
          </div>
          <div className="sm:col-span-2 flex justify-center pt-2">
            <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer active:scale-[0.98]">
              <i className="ri-file-chart-line"></i>
              Raporu Hazırla
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground-950">Hareket Görmeyen Ürünler</h2>
        <span className="text-sm text-foreground-500">{data.length} ürün bulundu</span>
      </div>

      <div className="hidden lg:block bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-background-100 border-b border-background-200/70">
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Ürün</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Kod</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Kategori</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Marka</th>
              <th className="px-4 py-3 text-center font-medium text-foreground-600">Stok</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-600">Depo</th>
              <th className="px-4 py-3 text-center font-medium text-foreground-600">Son Hareket</th>
              <th className="px-4 py-3 text-center font-medium text-foreground-600">Gün</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.code} className="border-b border-background-200/50 hover:bg-background-100/50 transition-colors">
                <td className="px-4 py-3 font-medium text-foreground-900">{item.name}</td>
                <td className="px-4 py-3 text-foreground-500">{item.code}</td>
                <td className="px-4 py-3 text-foreground-500">{item.category}</td>
                <td className="px-4 py-3 text-foreground-500">{item.brand}</td>
                <td className="px-4 py-3 text-center font-semibold text-foreground-900">{item.stock}</td>
                <td className="px-4 py-3 text-foreground-500">{item.warehouse}</td>
                <td className="px-4 py-3 text-center text-foreground-500">{item.lastMovement}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${item.daysAgo >= 90 ? "bg-accent-100 text-accent-700" : item.daysAgo >= 60 ? "bg-secondary-100 text-secondary-700" : "bg-primary-100 text-primary-700"}`}>
                    <i className="ri-time-line"></i>
                    {item.daysAgo} gün
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="lg:hidden space-y-3">
        {data.map((item) => (
          <div key={item.code} className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                <p className="text-xs text-foreground-400 mt-0.5">{item.code} · {item.category} · {item.brand}</p>
                <p className="text-xs text-foreground-400">{item.warehouse} · Stok: {item.stock}</p>
              </div>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${item.daysAgo >= 90 ? "bg-accent-100 text-accent-700" : item.daysAgo >= 60 ? "bg-secondary-100 text-secondary-700" : "bg-primary-100 text-primary-700"}`}>
                <i className="ri-time-line"></i>
                {item.daysAgo} gün
              </span>
            </div>
            <p className="text-xs text-foreground-500 mt-2">Son hareket: {item.lastMovement}</p>
          </div>
        ))}
      </div>
    </div>
  );
}